import { authFetch, getSessionToken } from "site/services/auth-session.service";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";

type EditorTextLockBody = {
  error?: string;
  holder_email?: string;
  expires_at_ms?: number;
};

export class EditorLessonLockError extends Error {
  readonly code = "lesson_locked";

  constructor(
    readonly holderEmail: string,
    readonly expiresAtMs: number,
  ) {
    super("lesson_locked");
    this.name = "EditorLessonLockError";
  }
}

function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  const h: Record<string, string> = {};
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function apiBase(): string {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  return base;
}

function lockUrl(textId: string): string {
  return `${apiBase()}/editor/texts/${encodeURIComponent(textId)}/lock`;
}

export async function editorLessonLockFromResponse(r: Response): Promise<EditorLessonLockError | null> {
  if (r.status !== 409) {
    return null;
  }
  const body = (await r.json().catch(() => ({}))) as EditorTextLockBody;
  return new EditorLessonLockError(body.holder_email?.trim() ?? "", body.expires_at_ms ?? 0);
}

export async function acquireEditorTextLock(textId: string): Promise<void> {
  const r = await authFetch(lockUrl(textId), {
    method: "POST",
    headers: authHeaders(),
  });
  const locked = await editorLessonLockFromResponse(r);
  if (locked) {
    throw locked;
  }
  if (!r.ok) {
    throw new Error(`editor lock acquire: HTTP ${r.status}`);
  }
}

export async function renewEditorTextLock(textId: string): Promise<void> {
  const r = await authFetch(lockUrl(textId), {
    method: "POST",
    headers: authHeaders(),
  });
  const locked = await editorLessonLockFromResponse(r);
  if (locked) {
    throw locked;
  }
  if (!r.ok) {
    throw new Error(`editor lock renew: HTTP ${r.status}`);
  }
}

export async function releaseEditorTextLock(textId: string): Promise<void> {
  try {
    const r = await authFetch(lockUrl(textId), {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!r.ok && r.status !== 404) {
      console.log(`editor lock release: HTTP ${r.status}`);
    }
  } catch (e) {
    console.log(e);
  }
}

export function formatEditorLockHolder(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) {
    return "другой редактор";
  }
  const at = trimmed.indexOf("@");
  if (at <= 0) {
    return trimmed;
  }
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (local.length <= 2) {
    return `${local}@${domain}`;
  }
  return `${local.slice(0, 2)}…@${domain}`;
}
